<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import HeaderView from '../components/HeaderView.vue'
import FooterView from '../components/FooterView.vue'
import DemoSection from '../components/DemoSection.vue'

type Billing = 'monthly' | 'annual'
type Currency = 'usd' | 'cop'

const billing = ref<Billing>('monthly')
const currency = ref<Currency>('usd')
const comparison = ref(false)
const expanded = reactive<Record<string, boolean>>({
  free: false, starter: false, pro: false, proplus: false, ent: false,
})
const faqOpen = reactive<Record<number, boolean>>({})

// Hover por columna en la tabla comparativa (0..4)
const hoverCol = ref<number | null>(null)
const planNames = ['F-Free', 'F-Starter', 'F-Pro', 'F-Pro Plus', 'F-Enterprise']

const RATE = 4000 // COP por USD

const isAnnual = computed(() => billing.value === 'annual')
const isCop = computed(() => currency.value === 'cop')
const cur = computed(() => (isCop.value ? 'COP' : 'USD'))

function fmt(usd: number): string {
  if (usd === 0) return '$0'
  const val = isCop.value ? usd * RATE : usd
  return '$' + val.toLocaleString('es-CO')
}

const subLabel = computed(() =>
  isAnnual.value ? `${cur.value} / mes · facturado anual` : `${cur.value} / mes`,
)

interface Price { main: string; sub: string; hasOriginal: boolean; original?: string }
function mk(m: number, orig?: number): Price {
  return orig != null
    ? { main: fmt(m), original: fmt(orig), sub: subLabel.value, hasOriginal: true }
    : { main: fmt(m), sub: subLabel.value, hasOriginal: false }
}

const prices = computed(() => ({
  free: { main: '$0', sub: `${cur.value} / mes`, hasOriginal: false } as Price,
  starter: isAnnual.value ? mk(79) : mk(59, 99),
  pro: isAnnual.value ? mk(199) : mk(149, 249),
  proplus: isAnnual.value ? mk(319) : mk(239, 399),
}))

const priceSize = computed(() => (isCop.value ? '32px' : '46px'))
const proSize = computed(() => (isCop.value ? '34px' : '48px'))
const origSize = computed(() => (isCop.value ? '16px' : '21px'))

function setBilling(b: Billing) {
  billing.value = b
  syncUrl('billing', b)
}
function setCurrency(c: Currency) {
  currency.value = c
  syncUrl('currency', c)
}
function syncUrl(key: string, val: string) {
  try {
    const u = new URL(location.href)
    u.searchParams.set(key, val)
    history.replaceState(null, '', u)
  } catch (e) {}
}
function toggleExp(k: string) {
  expanded[k] = !expanded[k]
}
function lbl(v: boolean | undefined) {
  return v ? 'Ver menos' : 'Ver todo'
}
function toggleFaq(i: number) {
  faqOpen[i] = !faqOpen[i]
}

onMounted(() => {
  try {
    const params = new URLSearchParams(location.search)
    const p = params.get('billing')
    if (p === 'annual' || p === 'monthly') billing.value = p
    const c = params.get('currency')
    if (c === 'usd' || c === 'cop') currency.value = c as Currency
  } catch (e) {}
})

const faqData = [
  { q: '¿Puedo cambiar de plan en cualquier momento?', a: 'Sí. El upgrade aplica de inmediato y el downgrade se hace efectivo en el siguiente ciclo de facturación, sin trámites.' },
  { q: '¿Qué pasa cuando termina el trial?', a: 'Puedes elegir el plan que mejor se ajuste a tu equipo o continuar en F-Free con las funciones básicas, sin perder tu información.' },
  { q: '¿Aceptan pago en pesos colombianos?', a: 'Sí. Procesamos pagos en pesos colombianos mediante MercadoPago: PSE, tarjeta de crédito y débito.' },
  { q: '¿Hay contratos anuales forzados?', a: 'No. Cancelas cuando quieras, sin penalidades ni cláusulas de permanencia.' },
  { q: '¿Qué pasa si supero mi límite de usuarios o activos?', a: 'El sistema te avisa con anticipación y te ofrece un add-on o un upgrade antes de bloquear cualquier función.' },
]

function rowCells(arr: string[]) {
  return arr.map((v, i) => ({
    v,
    c: v === '✓' ? '#129bef' : v === '—' ? '#C7D0D9' : '#33455B',
    bg: i === 2 ? '#F4F9FF' : 'transparent',
  }))
}

const tableRows = [
  { isCat: true, label: 'Órdenes de trabajo' },
  { isRow: true, label: 'Correctivas (ilimitadas)', cells: rowCells(['✓', '✓', '✓', '✓', '✓']) },
  { isRow: true, label: 'Preventivas / PMP', cells: rowCells(['—', '✓', '✓', '✓', '✓']) },
  { isCat: true, label: 'Activos' },
  { isRow: true, label: 'Gestión de activos', cells: rowCells(['50', '200', '500', '2.000', 'Ilimitado']) },
  { isRow: true, label: 'QR de activos', cells: rowCells(['—', '✓', '✓', '✓', '✓']) },
  { isCat: true, label: 'Usuarios' },
  { isRow: true, label: 'Máx. usuarios', cells: rowCells(['3', '5', '10', '25', 'Ilimitado']) },
  { isCat: true, label: 'Módulos especiales' },
  { isRow: true, label: 'Inventario', cells: rowCells(['—', '✓', '✓', '✓', '✓']) },
  { isRow: true, label: 'Gaby IA', cells: rowCells(['—', '—', '✓', '✓', '✓']) },
  { isRow: true, label: 'WhatsApp', cells: rowCells(['—', '—', '✓', '✓', '✓']) },
  { isRow: true, label: 'GPS / Mapas', cells: rowCells(['—', '—', '✓', '✓', '✓']) },
  { isRow: true, label: 'Templates', cells: rowCells(['—', '—', '✓', '✓', '✓']) },
  { isRow: true, label: 'Dashboard Financiero', cells: rowCells(['—', '—', '—', '✓', '✓']) },
  { isRow: true, label: 'Roles personalizados', cells: rowCells(['—', '—', '—', '✓', '✓']) },
  { isCat: true, label: 'Soporte' },
  { isRow: true, label: 'Email', cells: rowCells(['—', '✓', '✓', '✓', '✓']) },
  { isRow: true, label: 'Soporte prioritario', cells: rowCells(['—', '—', '—', '✓', '✓']) },
  { isRow: true, label: 'Onboarding dedicado', cells: rowCells(['—', '—', '—', '—', '✓']) },
]
</script>

<template>
  <div class="pricing-page">
    <HeaderView />

    <main class="pricing-main">
      <!-- HERO -->
      <section class="hero">
        <div class="hero-badge animate-fade-in-up">
          <span class="hero-badge-label">Planes</span>
          <span class="hero-badge-text">
            Precios simples y transparentes, sin contratos forzados
            <svg class="hero-badge-icon" fill="currentColor" viewBox="0 0 256 256">
              <path
                d="M216,120a8,8,0,0,1-8,8,64.07,64.07,0,0,0-64,64,8,8,0,0,1-16,0,64.07,64.07,0,0,0-64-64,8,8,0,0,1,0-16,64.07,64.07,0,0,0,64-64,8,8,0,0,1,16,0,64.07,64.07,0,0,0,64,64A8,8,0,0,1,216,120Z">
              </path>
            </svg>
            <span class="hero-badge-arrow">›</span>
          </span>
        </div>
        <h1 class="hero-title">
          Planes pensados para equipos de mantenimiento
          <span class="hero-title-accent">en crecimiento</span>
        </h1>
        <p class="hero-sub">Empieza gratis, crece cuando estés listo. Sin contratos anuales forzados.</p>

        <div class="toggles">
          <!-- Billing toggle (centrado) -->
          <div class="toggle billing">
            <div class="knob" :style="{ transform: isAnnual ? 'translateX(120px)' : 'translateX(0)' }"></div>
            <button class="toggle-btn" :style="{ color: isAnnual ? '#6B7A8D' : '#0F1B2E' }" @click="setBilling('monthly')">Mensual</button>
            <button class="toggle-btn" :style="{ color: isAnnual ? '#0F1B2E' : '#6B7A8D' }" @click="setBilling('annual')">Anual</button>
          </div>
          <!-- Currency switch circular (esquina superior derecha) -->
          <button class="currency-switch" @click="setCurrency(isCop ? 'usd' : 'cop')"
            :title="isCop ? 'Cambiar a USD' : 'Cambiar a COP'" :aria-label="isCop ? 'Moneda: COP' : 'Moneda: USD'">
            <span class="currency-flag">{{ isCop ? '🇨🇴' : '🇺🇸' }}</span>
            <span class="currency-code">{{ cur }}</span>
          </button>
        </div>
      </section>

      <!-- PLAN CARDS -->
      <section class="cards-section">
        <div class="cards-grid">
          <!-- F-FREE -->
          <div class="card">
            <div>
              <div class="card-name">F-Free</div>
              <div class="card-desc">Para arrancar y conocer la plataforma sin costo.</div>
            </div>
            <div class="plan-price">
              <div class="price-row"><span class="price-main" :style="{ fontSize: priceSize }">{{ prices.free.main }}</span></div>
              <div class="price-sub">{{ prices.free.sub }}</div>
            </div>
            <a href="/registro?plan=f-free" class="btn-outline">Empieza gratis</a>
            <ul class="feat-list">
              <li class="feat"><span class="check">✓</span><span>Órdenes correctivas ilimitadas</span></li>
              <li class="feat"><span class="check">✓</span><span>Hasta 50 activos</span></li>
              <li class="feat"><span class="check">✓</span><span>Hasta 3 usuarios</span></li>
              <li class="feat"><span class="check">✓</span><span>App web y móvil</span></li>
            </ul>
            <ul v-if="expanded.free" class="feat-list">
              <li class="feat"><span class="check">✓</span><span>Historial de mantenimiento</span></li>
              <li class="feat"><span class="check">✓</span><span>Reportes básicos</span></li>
            </ul>
            <button class="see-all" @click="toggleExp('free')">{{ lbl(expanded.free) }}</button>
          </div>

          <!-- F-STARTER -->
          <div class="card">
            <div>
              <div class="card-name">F-Starter</div>
              <div class="card-desc">Para equipos pequeños que formalizan su mantenimiento.</div>
            </div>
            <div class="plan-price">
              <div class="price-row">
                <span v-if="prices.starter.hasOriginal" class="price-orig" :style="{ fontSize: origSize }">{{ prices.starter.original }}</span>
                <span class="price-main" :style="{ fontSize: priceSize }">{{ prices.starter.main }}</span>
              </div>
              <div class="price-sub">{{ prices.starter.sub }}</div>
            </div>
            <a href="/registro?plan=f-starter" class="btn-outline">Empezar ahora</a>
            <ul class="feat-list">
              <li class="feat"><span class="check">✓</span><span class="bold">Todo lo de F-Free, más:</span></li>
              <li class="feat"><span class="check">✓</span><span>Órdenes preventivas / PMP</span></li>
              <li class="feat"><span class="check">✓</span><span>QR de activos</span></li>
              <li class="feat"><span class="check">✓</span><span>Inventario</span></li>
            </ul>
            <ul v-if="expanded.starter" class="feat-list">
              <li class="feat"><span class="check">✓</span><span>Hasta 200 activos</span></li>
              <li class="feat"><span class="check">✓</span><span>Hasta 5 usuarios</span></li>
              <li class="feat"><span class="check">✓</span><span>Soporte por email</span></li>
            </ul>
            <button class="see-all" @click="toggleExp('starter')">{{ lbl(expanded.starter) }}</button>
          </div>

          <!-- F-PRO -->
          <div class="card card-pro">
            <div class="popular-badge">Más popular</div>
            <div>
              <div class="card-name pro-name">F-Pro</div>
              <div class="card-desc">Para equipos que necesitan IA, automatización y más control.</div>
            </div>
            <div class="plan-price">
              <div class="price-row">
                <span v-if="prices.pro.hasOriginal" class="price-orig" :style="{ fontSize: origSize }">{{ prices.pro.original }}</span>
                <span class="price-main" :style="{ fontSize: proSize }">{{ prices.pro.main }}</span>
              </div>
              <div class="price-sub">{{ prices.pro.sub }}</div>
            </div>
            <a href="/registro?plan=f-pro" class="btn-solid">Empezar ahora</a>
            <ul class="feat-list">
              <li class="feat"><span class="check">✓</span><span class="bold">Todo lo de F-Starter, más:</span></li>
              <li class="feat"><span class="check">✓</span><span>Gaby IA</span></li>
              <li class="feat"><span class="check">✓</span><span>WhatsApp</span></li>
              <li class="feat"><span class="check">✓</span><span>GPS / Mapas</span></li>
            </ul>
            <ul v-if="expanded.pro" class="feat-list">
              <li class="feat"><span class="check">✓</span><span>Templates</span></li>
              <li class="feat"><span class="check">✓</span><span>Hasta 500 activos</span></li>
              <li class="feat"><span class="check">✓</span><span>Hasta 10 usuarios</span></li>
            </ul>
            <button class="see-all" @click="toggleExp('pro')">{{ lbl(expanded.pro) }}</button>
          </div>

          <!-- F-PRO PLUS -->
          <div class="card">
            <div>
              <div class="card-name">F-Pro Plus</div>
              <div class="card-desc">Para operaciones grandes con finanzas y roles avanzados.</div>
            </div>
            <div class="plan-price">
              <div class="price-row">
                <span v-if="prices.proplus.hasOriginal" class="price-orig" :style="{ fontSize: origSize }">{{ prices.proplus.original }}</span>
                <span class="price-main" :style="{ fontSize: priceSize }">{{ prices.proplus.main }}</span>
              </div>
              <div class="price-sub">{{ prices.proplus.sub }}</div>
            </div>
            <a href="/registro?plan=f-pro-plus" class="btn-outline">Empezar ahora</a>
            <ul class="feat-list">
              <li class="feat"><span class="check">✓</span><span class="bold">Todo lo de F-Pro, más:</span></li>
              <li class="feat"><span class="check">✓</span><span>Dashboard Financiero</span></li>
              <li class="feat"><span class="check">✓</span><span>Roles personalizados</span></li>
              <li class="feat"><span class="check">✓</span><span>Soporte prioritario</span></li>
            </ul>
            <ul v-if="expanded.proplus" class="feat-list">
              <li class="feat"><span class="check">✓</span><span>Hasta 2.000 activos</span></li>
              <li class="feat"><span class="check">✓</span><span>Hasta 25 usuarios</span></li>
            </ul>
            <button class="see-all" @click="toggleExp('proplus')">{{ lbl(expanded.proplus) }}</button>
          </div>

          <!-- F-ENTERPRISE -->
          <div class="card card-ent">
            <div>
              <div class="card-name">F-Enterprise</div>
              <div class="card-desc ent-desc">Para empresas con necesidades específicas y soporte dedicado.</div>
            </div>
            <div class="plan-price">
              <div class="ent-price">Precio a medida</div>
              <div class="ent-price-sub">Hablemos de tu operación</div>
            </div>
            <a href="https://wa.me/?text=Hola%2C%20quiero%20info%20del%20plan%20F-Enterprise" class="btn-white">Contactar ventas</a>
            <ul class="feat-list">
              <li class="feat ent-feat"><span class="check">✓</span><span class="bold ent-bold">Todo lo de F-Pro Plus, más:</span></li>
              <li class="feat ent-feat"><span class="check">✓</span><span>Activos ilimitados</span></li>
              <li class="feat ent-feat"><span class="check">✓</span><span>Usuarios ilimitados</span></li>
              <li class="feat ent-feat"><span class="check">✓</span><span>Onboarding dedicado</span></li>
            </ul>
            <ul v-if="expanded.ent" class="feat-list">
              <li class="feat ent-feat"><span class="check">✓</span><span>SLA y soporte dedicado</span></li>
              <li class="feat ent-feat"><span class="check">✓</span><span>Integraciones a medida</span></li>
            </ul>
            <button class="see-all" @click="toggleExp('ent')">{{ lbl(expanded.ent) }}</button>
          </div>
        </div>

        <div class="reassurance">
          <span><span class="check">✓</span> Actualizaciones incluidas</span>
          <span><span class="check">✓</span> Sin tarjeta para empezar</span>
          <span><span class="check">✓</span> Cancela cuando quieras</span>
        </div>
      </section>

      <!-- COMPARISON -->
      <section class="comparison-section">
        <button class="comp-toggle" @click="comparison = !comparison">
          {{ comparison ? 'Ocultar comparación de features' : 'Ver comparación completa de features' }}
          <span class="comp-arrow" :style="{ transform: comparison ? 'rotate(180deg)' : 'rotate(0deg)' }">▾</span>
        </button>

        <div class="comp-wrap" :class="{ open: comparison }" :style="{ maxHeight: comparison ? '2400px' : '0px' }">
          <div class="comp-table">
            <div class="comp-head">
              <div></div>
              <div v-for="(name, ci) in planNames" :key="name"
                class="comp-h" :class="{ 'comp-h-pro': ci === 2, 'col-hover': hoverCol === ci }"
                @mouseenter="hoverCol = ci" @mouseleave="hoverCol = null">
                {{ name }}
              </div>
            </div>
            <template v-for="(row, ri) in tableRows" :key="ri">
              <div v-if="row.isCat" class="comp-cat">{{ row.label }}</div>
              <div v-else class="comp-row">
                <div class="comp-row-label">{{ row.label }}</div>
                <div v-for="(cell, ci) in row.cells" :key="ci" class="comp-cell"
                  :class="{ 'col-hover': hoverCol === ci }" :style="{ color: cell.c, background: cell.bg }"
                  @mouseenter="hoverCol = ci" @mouseleave="hoverCol = null">{{ cell.v }}</div>
              </div>
            </template>
          </div>
        </div>
      </section>

      <!-- FAQ -->
      <section class="faq-section">
        <h2 class="faq-title">Preguntas frecuentes</h2>
        <p class="faq-sub">Todo lo que necesitas saber antes de empezar.</p>
        <div>
          <div v-for="(item, i) in faqData" :key="i" class="faq-item">
            <button class="faq-q" @click="toggleFaq(i)">
              <span class="faq-q-text">{{ item.q }}</span>
              <span class="faq-plus" :style="{ transform: faqOpen[i] ? 'rotate(45deg)' : 'rotate(0deg)' }">+</span>
            </button>
            <div class="faq-a-wrap" :style="{ maxHeight: faqOpen[i] ? '300px' : '0px' }">
              <p class="faq-a">{{ item.a }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- DEMO -->
      <DemoSection />
    </main>

    <FooterView />
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap');

.pricing-page {
  font-family: 'Manrope', system-ui, -apple-system, sans-serif;
  color: #0F1B2E;
  background: #fff;
}
/* Links sin subrayado, pero respetando el color propio de cada uno */
.pricing-page a { text-decoration: none; }

.pricing-main { padding-top: 0; }

/* HERO */
.hero {
  text-align: center;
  padding: 70px 28px 8px;
  max-width: 900px;
  margin: 0 auto;
}
.hero-badge {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background-color: #ffffff;
  margin-bottom: 24px;
}
@media (min-width: 768px) {
  .hero-badge {
    flex-direction: row;
  }
}
.hero-badge-label {
  background-color: #129BEF;
  color: #ffffff;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 9999px;
}
.hero-badge-text {
  font-size: 18px;
  color: #4B5563;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}
.hero-badge-icon {
  width: 16px;
  height: 16px;
  color: #F59E0B;
}
.hero-badge-arrow {
  color: #129BEF;
  font-weight: 700;
  margin-left: 4px;
  cursor: pointer;
}
.hero-badge-arrow:hover {
  text-decoration: underline;
}

/* Animación de entrada (igual que en home) */
.animate-fade-in-up {
  opacity: 0;
  animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
.hero-badge.animate-fade-in-up {
  animation-delay: 0.1s;
  animation-duration: 0.6s;
}
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.hero-title {
  font-size: clamp(33px, 5vw, 53px);
  line-height: 1.07;
  font-weight: 800;
  letter-spacing: -1.6px;
  margin: 0;
}
.hero-title-accent {
  color: #129bef;
  font-weight: 800;
}
.hero-sub {
  font-size: 19px;
  color: #5A6B7B;
  line-height: 1.55;
  margin: 22px auto 0;
  max-width: 600px;
}
.toggles {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  margin-top: 36px;
}
.currency-switch {
  position: absolute;
  top: 0;
  right: 0;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 1px solid #E2E8F0;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-family: inherit;
  transition: border-color .2s ease, transform .2s ease;
}
.currency-switch:hover {
  border-color: #129bef;
  transform: translateY(-2px);
}
.currency-flag {
  font-size: 22px;
  line-height: 1;
}
.currency-code {
  font-size: 10px;
  font-weight: 700;
  color: #6B7A8D;
  margin-top: 1px;
}
.toggle {
  display: inline-flex;
  align-items: center;
  background: #EEF3F9;
  border-radius: 999px;
  position: relative;
}
.toggle.billing { padding: 5px; }
.knob {
  position: absolute;
  top: 5px;
  left: 5px;
  width: 120px;
  height: calc(100% - 10px);
  background: #fff;
  border: 1px solid #E2E8F0;
  border-radius: 999px;
  transition: transform .3s cubic-bezier(.4,0,.2,1);
}
.toggle-btn {
  position: relative;
  z-index: 1;
  border: none;
  background: none;
  cursor: pointer;
  font-family: inherit;
  font-weight: 700;
  font-size: 15px;
  width: 120px;
  padding: 10px 0;
  border-radius: 999px;
  transition: color .2s;
}
.save-pill {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13.5px;
  font-weight: 700;
  color: #129bef;
  background: #EAF3FF;
  padding: 6px 14px;
  border-radius: 999px;
}

/* CARDS */
.cards-section { padding: 40px 28px 8px; }
.cards-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  max-width: 1340px;
  margin: 0 auto;
  align-items: stretch;
  border-radius: 18px;
  overflow: hidden;
}
.card {
  background: #fff;
  border: 1px solid #E4E4E7;
  margin: 0 0 0 -1px;
  padding: 30px 22px 26px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}
/* Iguala la altura del bloque de precio para alinear los CTA de todas las cards */
.plan-price {
  min-height: 78px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}
.card:first-child {
  margin-left: 0;
  border-top-left-radius: 18px;
  border-bottom-left-radius: 18px;
}
.card:last-child {
  border-top-right-radius: 18px;
  border-bottom-right-radius: 18px;
}
.card-pro {
  background: #F4F9FF;
  border: 1.5px solid #129bef;
  padding: 30px 22px 26px;
  position: relative;
  z-index: 1;
}
.card-ent {
  background: #0F1B2E;
  border: 1px solid #0F1B2E;
  color: #fff;
}

/* Tablet: la cuadrícula conectada deja de funcionar al envolver -> cards separadas */
@media (max-width: 1024px) {
  .cards-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
    overflow: visible;
    border-radius: 0;
  }
  .card {
    margin: 0;
    border: 1px solid #E4E4E7;
    border-radius: 16px;
  }
  .card:first-child,
  .card:last-child {
    border-radius: 16px;
  }
}

@media (max-width: 720px) {
  .cards-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
}
.popular-badge {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  background: #129bef;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .4px;
  padding: 6px 16px;
  border-radius: 0 0 12px 12px;
  white-space: nowrap;
}
.card-name { font-size: 20px; font-weight: 800; }
.pro-name { color: #129bef; }
.card-desc {
  font-size: 13.5px;
  color: #6B7A8D;
  line-height: 1.45;
  margin-top: 5px;
  min-height: 40px;
}
.ent-desc { color: #A9B6C6; }
.price-row {
  display: flex;
  align-items: baseline;
  gap: 9px;
  flex-wrap: wrap;
}
.price-main { font-weight: 800; letter-spacing: -1.6px; }
.price-orig {
  color: #9AA7B5;
  text-decoration: line-through;
  font-weight: 700;
}
.price-sub {
  font-size: 13px;
  color: #6B7A8D;
  font-weight: 600;
  margin-top: 2px;
}
.ent-price { font-size: 30px; line-height: 1.15; font-weight: 800; letter-spacing: -1.2px; }
.ent-price-sub { font-size: 13px; color: #A9B6C6; font-weight: 600; margin-top: 2px; }

.btn-outline, .btn-solid, .btn-white {
  display: block;
  text-align: center;
  font-weight: 700;
  font-size: 15px;
  border-radius: 999px;
}
.btn-outline {
  padding: 13px;
  border: 1.5px solid #129bef;
  color: #129bef;
  background: #fff;
}
.btn-solid {
  padding: 14px;
  border: 1.5px solid transparent;
  color: #fff;
  background: #129bef;
}
.btn-white {
  padding: 13px;
  border: 1.5px solid transparent;
  color: #0F1B2E;
  background: #fff;
}

.feat-list {
  display: flex;
  padding: 0;
  flex-direction: column;
  gap: 11px;
}
.feat-list:first-of-type { margin-top: 4px; }
.feat {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 14px;
  line-height: 1.45;
  color: #3A4A5E;
}
.ent-feat { color: #D4DDE8; }
.check { color: #129bef; font-weight: 800; flex: none; margin-top: 1px; }
.bold { font-weight: 600; }
.ent-bold { color: #fff; }
.see-all {
  background: none;
  border: none;
  color: #129bef;
  font-weight: 700;
  font-size: 13.5px;
  cursor: pointer;
  text-align: left;
  padding: 0;
  font-family: inherit;
  margin-top: auto;
}
.reassurance {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px 28px;
  max-width: 1100px;
  margin: 30px auto 0;
  font-size: 13.5px;
  font-weight: 600;
  color: #6B7A8D;
}
.reassurance span {
  display: flex;
  align-items: center;
  gap: 7px;
}

/* COMPARISON */
.comparison-section { padding: 54px 28px 8px; text-align: center; }
.comp-toggle {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1.5px solid #D7E1EC;
  color: #0F1B2E;
  font-family: inherit;
  font-weight: 700;
  font-size: 15px;
  padding: 13px 24px;
  border-radius: 999px;
  cursor: pointer;
}
.comp-arrow {
  display: inline-flex;
  transition: transform .3s;
  color: #129bef;
}
.comp-wrap {
  max-width: 1100px;
  margin: 0 auto;
  overflow: hidden;
  transition: max-height .5s cubic-bezier(.4,0,.2,1);
}
/* Abierta: dejamos overflow visible para que el encabezado sea sticky respecto al viewport */
.comp-wrap.open {
  overflow: visible;
}
.comp-table {
  border: 1px solid #E6ECF2;
  border-radius: 18px;
  margin-top: 22px;
  text-align: left;
  background: #fff;
}
.comp-head {
  position: sticky;
  top: 64px;
  z-index: 5;
  display: grid;
  grid-template-columns: minmax(180px, 1.6fr) repeat(5, 1fr);
  align-items: end;
  background: #fff;
  padding: 4px 0 0;
  border-top-left-radius: 18px;
  border-top-right-radius: 18px;
  box-shadow: 0 1px 0 #E6ECF2;
}
.comp-h {
  text-align: center;
  padding: 14px 6px;
  font-weight: 800;
  font-size: 14px;
  color: #0F1B2E;
}
.comp-h-pro {
  color: #129bef;
  background: #F4F9FF;
  border-radius: 12px 12px 0 0;
}
/* Hover por columna: tinte que respeta el background inline de cada celda */
.comp-h,
.comp-cell {
  transition: box-shadow .15s ease;
}
.comp-h.col-hover,
.comp-cell.col-hover {
  box-shadow: inset 0 0 0 9999px rgba(18, 155, 239, 0.08);
}
.comp-cat {
  background: #F5F8FC;
  padding: 11px 20px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .7px;
  text-transform: uppercase;
  color: #129bef;
  border-top: 1px solid #E6ECF2;
}
.comp-row {
  display: grid;
  grid-template-columns: minmax(180px, 1.6fr) repeat(5, 1fr);
  align-items: center;
  border-top: 1px solid #EEF2F6;
}
.comp-row-label {
  padding: 13px 20px;
  font-size: 14px;
  font-weight: 600;
  color: #3A4A5E;
}
.comp-cell {
  padding: 13px 8px;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
}

/* FAQ */
.faq-section { padding: 64px 28px 20px; max-width: 760px; margin: 0 auto; }
.faq-title {
  font-size: clamp(26px, 3.4vw, 36px);
  font-weight: 800;
  letter-spacing: -1px;
  text-align: center;
  margin: 0 0 8px;
}
.faq-sub { text-align: center; color: #6B7A8D; font-size: 16px; margin: 0 0 28px; }
.faq-item { border-bottom: 1px solid #EBEFF4; }
.faq-q {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  padding: 22px 4px;
}
.faq-q-text { font-size: 17px; font-weight: 700; color: #0F1B2E; }
.faq-plus {
  flex: none;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #129bef;
  font-size: 24px;
  font-weight: 500;
  line-height: 1;
  transition: transform .3s;
}
.faq-a-wrap { overflow: hidden; transition: max-height .35s ease; }
.faq-a {
  margin: 0;
  padding: 0 4px 24px;
  font-size: 16px;
  line-height: 1.6;
  color: #5A6B7B;
}
.faq-item:last-child {
  border-bottom: none;
}
/* Tabla scroll horizontal en mobile */
@media (max-width: 768px) {
  .comp-table { min-width: 680px; }
  .comp-wrap.open { overflow: auto; -webkit-overflow-scrolling: touch; }

  .hero { padding: 48px 18px 4px; }
  .hero-sub { font-size: 17px; }
  .cards-section { padding: 28px 16px 4px; }
  .comparison-section { padding: 40px 16px 4px; }
  .faq-section { padding: 48px 18px 16px; }
}
</style>
